/**
 * DocumentControlContent - AS9100D Document Control Center
 * 
 * Central hub for company-controlled documents (Doc Control scope):
 * - Company Documents (global, policies/procedures/templates)
 * - Project Certification Documents (user-scoped by project permissions)
 * - Design Documents (user-scoped by project permissions)
 * - Shared Design Library (global engineering templates)
 * 
 * Note: Reference Library (industry standards, technical specs) is separate
 * and managed by engineers as their personal technical reference toolbox.
 * 
 * Features:
 * - Recently Opened section (uses global aeroRecentDocuments)
 * - Show first 5 per category with "Show all" expand
 * - Search/filter across all document types
 * - Click to open document in viewer
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui';
import type { ProjectInstance, DesignDocument, CompanyDocument } from '../../types';
import { logger } from '../../utils/logger';

// Unified document type for the control center
export interface ControlledDocument {
  id: string;
  documentNumber: string;
  documentTitle: string;
  projectSpecificTitle?: string;
  projectId?: string;
  projectName?: string;
  projectCode?: string;
  category: DocumentCategory;
  docType?: string;
  documentType?: string;
  currentRevision?: string;
  status?: string;
  scope?: string;
  standardNumber?: string;
  indexStatus?: string;
}

// Document Control categories (AS9100D managed documents only)
// Note: 'reference' removed - Reference Library is engineer's personal toolbox
export type DocumentCategory = 
  | 'recent'
  | 'company' 
  | 'certification' 
  | 'design' 
  | 'shared-design';

interface DocumentGroup {
  category: DocumentCategory;
  label: string;
  icon: string;
  documents: ControlledDocument[];
  isExpanded: boolean;
  showAll: boolean;
}

export interface DocumentControlContentProps {
  projects: ProjectInstance[];
  designDocs: DesignDocument[];
  companyDocs: CompanyDocument[];
  loading: boolean;
  isSearching: boolean;
  searchQuery: string;
  onDocumentClick: (doc: ControlledDocument) => void;
}

// Use the global recent documents key shared across the app
const RECENT_STORAGE_KEY = 'aeroRecentDocuments';
const ITEMS_TO_SHOW = 5;

export function DocumentControlContent({
  projects,
  designDocs,
  companyDocs,
  loading,
  isSearching,
  searchQuery,
  onDocumentClick,
}: DocumentControlContentProps) {
  // Track which categories show all items
  const [showAllMap, setShowAllMap] = useState<Record<string, boolean>>({});
  const [recentDocIds, setRecentDocIds] = useState<string[]>([]);
  
  // Load recent documents from global localStorage
  // Refresh when component re-renders or data changes
  useEffect(() => {
    const loadRecent = () => {
      try {
        const saved = localStorage.getItem(RECENT_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Handle both formats: array of strings or array of objects with id
          // Filter to only Doc Control scoped documents (company, cert, design)
          interface RecentDocItem {
            id?: string;
            documentId?: string;
            documentNumber?: string;
          }
          const docControlIds = parsed
            .filter((item: string | RecentDocItem) => {
              const id = typeof item === 'string' ? item : item.id;
              // Only include Doc Control scope: company, certification (cert), design
              // Exclude reference docs (ref-) as those belong in Reference Library
              return id && (
                id.startsWith('company-') || 
                id.startsWith('cert-') || 
                id.startsWith('design-')
              );
            })
            .map((item: string | RecentDocItem) => 
              typeof item === 'string' ? item : (item.id || item.documentId || item.documentNumber)
            )
            .filter(Boolean);
          setRecentDocIds(docControlIds);
        }
      } catch (e) {
        logger.error('Failed to load recent documents:', e);
      }
    };
    
    // Load initially
    loadRecent();
    
    // Also listen for storage events (from other tabs) and poll for same-tab changes
    const handleStorage = (e: StorageEvent) => {
      if (e.key === RECENT_STORAGE_KEY) {
        loadRecent();
      }
    };
    window.addEventListener('storage', handleStorage);
    
    // Poll every 2 seconds to catch same-tab localStorage changes
    const interval = setInterval(loadRecent, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);
  
  // Loading state
  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-16 bg-muted rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }
  
  // Searching state
  if (isSearching) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin text-3xl mb-3">🔍</div>
        <p className="text-muted-foreground">Searching all documents...</p>
      </div>
    );
  }
  
  // Build the complete document list from all sources (AS9100D managed docs only)
  // Note: Reference documents are in Reference Library (engineer's toolbox)
  const allDocuments: ControlledDocument[] = [];
  
  // 1. Company Documents (global - policies, procedures, templates)
  (companyDocs || []).forEach(doc => {
    allDocuments.push({
      id: `company-${doc.documentId}`,
      documentNumber: doc.documentId,
      documentTitle: doc.title,
      category: 'company',
      docType: doc.category || 'Policy',
      currentRevision: doc.currentRevision,
      status: doc.status || 'Active',
    });
  });
  
  // 2. Certification documents from projects (user-scoped)
  (projects || []).forEach(project => {
    if (project.documents) {
      project.documents.forEach(doc => {
        allDocuments.push({
          id: `cert-${project.id}-${doc.documentNumber}`,
          documentNumber: doc.documentNumber,
          documentTitle: doc.documentTitle,
          projectSpecificTitle: doc.projectSpecificTitle,
          projectId: project.id,
          projectName: project.name,
          projectCode: project.projectCode,
          category: 'certification',
          docType: doc.docType,
          currentRevision: doc.currentRevision,
          status: doc.status,
        });
      });
    }
  });
  
  // 3. Design documents (user-scoped or shared)
  (designDocs || []).forEach(doc => {
    const project = projects?.find(p => p.id === doc.projectId);
    allDocuments.push({
      id: `design-${doc.documentNumber}`,
      documentNumber: doc.documentNumber,
      documentTitle: doc.documentTitle,
      projectId: doc.projectId || undefined,
      projectName: project?.name || (doc.scope === 'shared' ? 'Shared Library' : undefined),
      projectCode: doc.projectCode,
      category: doc.scope === 'shared' ? 'shared-design' : 'design',
      documentType: doc.documentType,
      currentRevision: doc.currentRevision,
      status: doc.status,
      scope: doc.scope,
    });
  });
  
  // Filter by search query
  const filteredDocuments = searchQuery.trim()
    ? allDocuments.filter(doc => {
        const query = searchQuery.toLowerCase();
        return (
          (doc.documentNumber || '').toLowerCase().includes(query) ||
          (doc.documentTitle || '').toLowerCase().includes(query) ||
          (doc.projectSpecificTitle || '').toLowerCase().includes(query) ||
          (doc.projectName || '').toLowerCase().includes(query) ||
          (doc.projectCode || '').toLowerCase().includes(query) ||
          (doc.standardNumber || '').toLowerCase().includes(query) ||
          (doc.docType || '').toLowerCase().includes(query)
        );
      })
    : allDocuments;
  
  // Build recent documents from global localStorage
  // Match against our document list by various ID patterns
  const recentDocuments = recentDocIds
    .map(recentId => {
      // Try to find a match in allDocuments
      return allDocuments.find(d => {
        // Match by full id
        if (d.id === recentId) return true;
        // Match by documentNumber
        if (d.documentNumber === recentId) return true;
        // Match by id without prefix
        if (d.id.replace(/^(ref|company|cert|design)-/, '') === recentId) return true;
        // Match by partial (for cert- which has project id)
        if (d.id.includes(recentId)) return true;
        return false;
      });
    })
    .filter((d): d is ControlledDocument => d !== undefined)
    .slice(0, 10);
  
  // Empty state
  if (allDocuments.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">🗂️</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Document Control Center
        </h3>
        <p className="text-muted-foreground mb-4">
          No documents available yet.
        </p>
      </div>
    );
  }
  
  // No search results
  if (searchQuery.trim() && filteredDocuments.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-4xl mb-3">📭</div>
        <p className="text-muted-foreground">No documents match "{searchQuery}"</p>
      </div>
    );
  }
  
  // Group documents by category - all expanded by default
  const allGroups: DocumentGroup[] = [
    // Recently Opened (only show when not searching and has items)
    ...(!searchQuery.trim() && recentDocuments.length > 0 ? [{
      category: 'recent' as const,
      label: 'Recently Opened',
      icon: '🕐',
      documents: recentDocuments,
      isExpanded: true,
      showAll: showAllMap['recent'] || false,
    }] : []),
    // Company Documents (global - policies, procedures, templates)
    {
      category: 'company' as const,
      label: 'Company Documents',
      icon: '🏢',
      documents: filteredDocuments.filter(d => d.category === 'company'),
      isExpanded: true,
      showAll: showAllMap['company'] || false,
    },
    // Certification Documents (user-scoped)
    {
      category: 'certification' as const,
      label: 'Certification Documents',
      icon: '📋',
      documents: filteredDocuments.filter(d => d.category === 'certification'),
      isExpanded: true,
      showAll: showAllMap['certification'] || false,
    },
    // Design Documents (user-scoped)
    {
      category: 'design' as const,
      label: 'Project Design Documents',
      icon: '📐',
      documents: filteredDocuments.filter(d => d.category === 'design'),
      isExpanded: true,
      showAll: showAllMap['design'] || false,
    },
    // Shared Design Library (global)
    {
      category: 'shared-design' as const,
      label: 'Shared Design Library',
      icon: '🔗',
      documents: filteredDocuments.filter(d => d.category === 'shared-design'),
      isExpanded: true,
      showAll: showAllMap['shared-design'] || false,
    },
  ];
  
  const groups = allGroups.filter(g => g.documents.length > 0);
  
  const toggleShowAll = (category: string) => {
    setShowAllMap(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };
  
  // Count documents by scope
  const globalCount = filteredDocuments.filter(d => 
    d.category === 'reference' || d.category === 'company' || d.category === 'shared-design'
  ).length;
  const projectCount = filteredDocuments.filter(d => 
    d.category === 'certification' || d.category === 'design'
  ).length;
  
  return (
    <div className="p-4 space-y-4">
      {/* Summary stats */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {searchQuery.trim() 
            ? `Found ${filteredDocuments.length} document${filteredDocuments.length !== 1 ? 's' : ''}`
            : `${allDocuments.length} total documents`
          }
        </span>
        {!searchQuery.trim() && (
          <div className="flex gap-3 text-xs">
            <span className="text-info">
              {globalCount} global
            </span>
            <span className="text-success">
              {projectCount} project
            </span>
          </div>
        )}
      </div>
      
      {/* Document groups */}
      {groups.map(group => (
        <DocumentGroupComponent
          key={group.category}
          group={group}
          itemsToShow={ITEMS_TO_SHOW}
          onToggleShowAll={() => toggleShowAll(group.category)}
          onDocumentClick={onDocumentClick}
        />
      ))}
    </div>
  );
}

// Document group component
interface DocumentGroupProps {
  group: DocumentGroup;
  itemsToShow: number;
  onToggleShowAll: () => void;
  onDocumentClick: (doc: ControlledDocument) => void;
}

function DocumentGroupComponent({ group, itemsToShow, onToggleShowAll, onDocumentClick }: DocumentGroupProps) {
  const isRecent = group.category === 'recent';
  const visibleDocs = group.showAll ? group.documents : group.documents.slice(0, itemsToShow);
  const hasMore = group.documents.length > itemsToShow;
  const hiddenCount = group.documents.length - itemsToShow;
  
  return (
    <div className={`border rounded-lg overflow-hidden ${
      isRecent 
        ? 'border-info bg-info-light' 
        : 'border-border'
    }`}>
      {/* Group header */}
      <div className={`flex items-center justify-between px-4 py-2 ${
        isRecent
          ? 'bg-info-light'
          : 'bg-muted/30'
      }`}>
        <div className="flex items-center gap-2">
          <span>{group.icon}</span>
          <span className={`font-medium ${
            isRecent ? 'text-info' : 'text-foreground'
          }`}>
            {group.label}
          </span>
          <span className={`text-sm ${
            isRecent ? 'text-info' : 'text-muted-foreground'
          }`}>
            ({group.documents.length})
          </span>
        </div>
      </div>
      
      {/* Documents */}
      <div className="divide-y divide-gray-100 dark:divide-slate-700">
        {visibleDocs.map(doc => (
          <DocumentCard
            key={doc.id}
            document={doc}
            onClick={() => onDocumentClick(doc)}
            isRecent={isRecent}
          />
        ))}
      </div>
      
      {/* Show more/less button */}
      {hasMore && (
        <Button
          onClick={onToggleShowAll}
          variant="ghost"
          className={`w-full py-2 text-sm font-medium h-auto rounded-none ${
            isRecent
              ? 'text-info hover:bg-info'
              : 'text-muted-foreground hover:bg-muted/50'
          }`}
        >
          {group.showAll ? '▲ Show less' : `▼ Show all ${group.documents.length} (${hiddenCount} more)`}
        </Button>
      )}
    </div>
  );
}

// Document card component
interface DocumentCardProps {
  document: ControlledDocument;
  onClick: () => void;
  isRecent?: boolean;
}

function DocumentCard({ document, onClick, isRecent }: DocumentCardProps) {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Released':
      case 'Indexed':
      case 'completed':
        return 'bg-success-light text-success';
      case 'Approved':
      case 'Active':
        return 'bg-info-light text-info';
      case 'Draft':
      case 'pending':
        return 'bg-warning-light text-warning';
      case 'In Review':
      case 'processing':
        return 'bg-secondary text-secondary-foreground';
      case 'failed':
        return 'bg-destructive-light text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  
  const getCategoryColor = (category: DocumentCategory) => {
    switch (category) {
      case 'reference':
        return 'bg-info-light text-info';
      case 'company':
        return 'bg-success-light text-success';
      case 'certification':
        return 'bg-info-light text-info';
      case 'design':
        return 'bg-info-light text-info';
      case 'shared-design':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  
  return (
    <div
      className={`p-3 cursor-pointer transition-colors ${
        isRecent 
          ? 'hover:bg-primary/90/50 dark:hover:bg-primary/90/20' 
          : 'hover:bg-muted/50'
      }`}
      onClick={onClick}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-mono text-sm font-medium text-foreground truncate">
              {document.standardNumber || document.documentNumber}
            </h4>
            {document.currentRevision && (
              <span className="text-xs px-1 py-0.5 bg-muted text-foreground rounded font-mono">
                {document.currentRevision}
              </span>
            )}
            {/* Show category badge in recent section */}
            {isRecent && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${getCategoryColor(document.category)}`}>
                {document.category === 'shared-design' ? 'shared' : document.category}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {document.projectSpecificTitle || document.documentTitle}
          </p>
        </div>
        
        {/* Status badge */}
        {document.status && (
          <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full ${getStatusColor(document.status)}`}>
            {document.status}
          </span>
        )}
      </div>
      
      {/* Metadata row */}
      {(document.projectCode || document.projectName || (!isRecent && (document.docType || document.documentType))) && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {/* Project info */}
          {(document.projectCode || document.projectName) && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <span className="text-muted-foreground">📁</span>
              <span className="text-foreground">
                {document.projectCode || document.projectName}
              </span>
            </div>
          )}
          
          {/* Document type */}
          {(document.docType || document.documentType) && !isRecent && (
            <span className={`px-1 py-0.5 rounded ${getCategoryColor(document.category)}`}>
              {document.docType || document.documentType}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default DocumentControlContent;
